// As setas de rolagem dos meses da EAP (change 00, D13). A árvore é o único contêiner de
// rolagem horizontal; a coluna da EAP fica presa à esquerda e os meses de todas as linhas
// rolam juntos. Uma seta aparece só na extremidade que tem meses fora da tela e rola um bloco
// (os meses visíveis menos um). As setas ← e → do teclado fazem o mesmo com o foco na árvore,
// e a posição volta depois de uma recarga que encolheu a árvore por um instante.
import { useCallback, useEffect, useRef, useState } from "react";

// A coluna da EAP (`.accordion-sticky-header`) e o passo de um mês (95 px + o espaço de 16 px
// entre as colunas): o bloco rolado é medido nos meses que cabem ao lado da coluna fixa.
export const IDENTITY_WIDTH = 420;
export const MONTH_PITCH = 111;

export const scrollEdgesOf = (element) => {
  if (!element) return { before: false, after: false };
  const { scrollLeft, scrollWidth, clientWidth } = element;
  return {
    before: scrollLeft > 1,
    after: scrollLeft + clientWidth < scrollWidth - 1,
  };
};

export const scrollBlockOf = (element) => {
  const visibleMonths = Math.floor(Math.max(0, (element?.clientWidth ?? 0) - IDENTITY_WIDTH) / MONTH_PITCH);
  return Math.max(1, visibleMonths - 1) * MONTH_PITCH;
};

const MonthScrollEdges = ({ children, className = "", hidden = false }) => {
  const container = useRef(null);
  // A posição que o cliente escolheu: uma recarga que encolhe a árvore não a perde.
  const wanted = useRef(0);
  const [edges, setEdges] = useState({ before: false, after: false });

  const measure = useCallback(() => {
    const element = container.current;
    if (!element) return;
    if (element.scrollLeft < wanted.current && element.scrollWidth - element.clientWidth >= wanted.current) {
      element.scrollLeft = wanted.current;
    }
    const next = scrollEdgesOf(element);
    setEdges((current) => (current.before === next.before && current.after === next.after ? current : next));
  }, []);

  useEffect(() => {
    const element = container.current;
    if (!element) return undefined;
    measure();
    if (typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    for (const child of element.children) observer.observe(child);
    return () => observer.disconnect();
  });

  const scrollBy = (direction) => {
    const element = container.current;
    if (!element) return;
    const left = direction * scrollBlockOf(element);
    wanted.current = Math.max(0, element.scrollLeft + left);
    element.scrollBy?.({ left, behavior: "smooth" });
  };

  return (
    <div className={`ampli-month-scroll${edges.before ? " is-scrolled" : ""}`} hidden={hidden}>
      {edges.before ? (
        <button
          type="button"
          className="ampli-month-scroll__edge is-before"
          aria-label="Ver meses anteriores"
          onClick={() => scrollBy(-1)}
        >
          <i className="icon icon-width-16 icon-angle-left" aria-hidden="true" />
        </button>
      ) : null}
      <div
        ref={container}
        className={className}
        // A área dos meses rola também pelo teclado: o foco nela e as setas ← e →.
        tabIndex={0}
        role="region"
        aria-label="Meses da EAP"
        data-testid="eap-month-scroll"
        onScroll={() => {
          wanted.current = container.current?.scrollLeft ?? 0;
          measure();
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            event.preventDefault();
            scrollBy(event.key === "ArrowRight" ? 1 : -1);
          }
        }}
      >
        {children}
      </div>
      {edges.after ? (
        <button
          type="button"
          className="ampli-month-scroll__edge is-after"
          aria-label="Ver meses seguintes"
          onClick={() => scrollBy(1)}
        >
          <i className="icon icon-width-16 icon-angle-right" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

export default MonthScrollEdges;
